import NeptuneElement from '../../NeptuneElement.js';

class ErrorPage extends NeptuneElement {

	static ERROR_MESSAGES_URL = '/src/components/chrome/assets/errorMessage.json';
	static DEFAULT_HOME_PAGE_URL = '/';

	/** @type {Map<string, Promise<object>>} Shared fetch-promise cache for error messages JSON, keyed by URL. */
	static #errorMessagesCache = (window.__neptuneErrorMessagesCache ??= new Map());

	templateMarkup = `
		<section>
			<h1></h1>
			<p></p>
			<div>
				<neptune-button type="primary" size="default" icon-start="fa-arrow-left"> Back to previous page </neptune-button>
				<neptune-button type="secondary" size="default"> Return to home page </neptune-button>
			</div>
		</section>
	`;

	componentCssUrl = '/src/components/chrome/errorPage/error-page.css';

	errorMessages = {};

	async connectedCallback() {
		await super.connectedCallback();
		await this.loadErrorMessages();
        this.processInitialAttributesAfterRender(ErrorPage.observedAttributes);
		this.updateErrorContent();
	}

	static get observedAttributes() {
		return ['error-code', 'home-page-url'];
	}

	async setEventListeners() {
		this.backButton = this.shadowRoot?.querySelector('section > div > neptune-button:first-of-type');
		this.homeButton = this.shadowRoot?.querySelector('section > div > neptune-button:last-of-type');

		this.handleBackClickBound = this.handleBackClick.bind(this);
		this.handleHomeClickBound = this.handleHomeClick.bind(this);

		this.backButton?.addEventListener('click', this.handleBackClickBound);
		this.homeButton?.addEventListener('click', this.handleHomeClickBound);
	}

	async removeEventListeners() {
		this.backButton?.removeEventListener('click', this.handleBackClickBound);
		this.homeButton?.removeEventListener('click', this.handleHomeClickBound);

		this.backButton = null;
		this.homeButton = null;
		this.handleBackClickBound = null;
		this.handleHomeClickBound = null;
	}

	attributeChangedCallback(name, oldValue, newValue) {
		if (!this.isRendered || oldValue === newValue) {
			return;
		}

		this.processAttribute(name, oldValue, newValue);
	}

	processAttribute(name) {
		if (name === 'error-code') {
			this.updateErrorContent();
		}
	}

	getHomePageUrl() {
		const homePageUrl = (this.getAttribute('home-page-url') || '').trim();
		return homePageUrl || ErrorPage.DEFAULT_HOME_PAGE_URL;
	}

	async loadErrorMessages() {
		const url = ErrorPage.ERROR_MESSAGES_URL;
		if (!ErrorPage.#errorMessagesCache.has(url)) {
			const messagesPromise = fetch(url)
				.then(async response => {
					if (!response.ok) return {};
					const parsed = await response.json();
					return parsed ?? {};
				})
				.catch(() => ({}));
			ErrorPage.#errorMessagesCache.set(url, messagesPromise);
		}
		this.errorMessages = await ErrorPage.#errorMessagesCache.get(url);
	}

	updateErrorContent() {
		const errorHeading = this.shadowRoot?.querySelector('section > h1');
		const errorMessage = this.shadowRoot?.querySelector('section > p');

		if (!errorHeading || !errorMessage) {
			return;
		}

		const errorCode = this.getAttribute('error-code') || '404';
		const messageConfig = this.errorMessages[errorCode] || this.errorMessages['404'];

		const title = messageConfig?.title || 'Unknown Error';
		const message = this.getAttribute('error-message') || messageConfig?.message || 'Something went wrong while loading this page.';

		errorHeading.textContent = `Error Code ${errorCode}: ${title}`;
		errorMessage.textContent = message;
	}

	handleBackClick() {
		if (window.history.length > 1) {
			window.history.back();
			return;
		}

		this.navigateTo(this.getHomePageUrl());
	}

	handleHomeClick() {
		this.navigateTo(this.getHomePageUrl());
	}

	navigateTo(url) {
		window.location.assign(url);
	}
}

customElements.define('neptune-error-page', ErrorPage);
export default ErrorPage;